/**
 * Hook: useFinancialPaymentLinks
 * 
 * CRUD de links de pagamento com integração via edge function financial-payment-links
 * Queries paginadas, mutations para criar/editar/remover/restaurar/ativar/desativar
 * Gestão de imagens (upload, remoção, definir principal)
 * 
 * RELEASE: 2026-03-21 v1.0.0 - Criação do hook
 * @author Módulo Financeiro - Links de Pagamento
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { createLogger } from '@/lib/logger';
import { toast } from 'sonner';

const logger = createLogger('useFinancialPaymentLinks');

// ============= TIPOS =============

/** Dados de um link de pagamento armazenado localmente */
export interface PaymentLink {
  id: string;
  tenant_id: string;
  conta_corrente_id: string;
  provider_link_id: string | null;
  link_url: string | null;
  name: string;
  description: string | null;
  value: number | null;
  billing_type: string;
  charge_type: string;
  max_installment_count: number | null;
  due_date_limit_days: number | null;
  notification_enabled: boolean;
  is_address_required: boolean;
  end_date: string | null;
  external_reference: string | null;
  callback_config: { successUrl?: string; autoRedirect?: boolean } | null;
  active: boolean;
  deleted: boolean;
  view_count: number;
  created_at: string;
  updated_at: string;
}

/** Payload para criar/editar link */
export interface PaymentLinkFormData {
  name: string;
  description?: string;
  value?: number;
  billing_type: string;
  charge_type: string;
  max_installment_count?: number;
  due_date_limit_days?: number;
  notification_enabled?: boolean;
  is_address_required?: boolean;
  end_date?: string;
  external_reference?: string;
  callback_config?: { successUrl?: string; autoRedirect?: boolean };
}

/** Imagem do link de pagamento (retornada pelo Asaas, já mapeada para estrutura plana) */
export interface PaymentLinkImage {
  id: string;
  /** Indica se é a imagem principal do link */
  main?: boolean;
  /** URL proxied via edge function (data:image/...;base64,...) — fonte primária */
  proxiedUrl?: string;
  previewUrl: string;
  downloadUrl: string;
  originalName: string;
  size: number;
  extension: string;
}

// ============= HOOK PRINCIPAL =============

export function useFinancialPaymentLinks(contaCorrenteId?: string) {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  // Chave base para queries
  const queryKey = ['financial-payment-links', contaCorrenteId];

  /**
   * Query: Lista links de pagamento do banco local
   */
  const linksQuery = useQuery({
    queryKey,
    queryFn: async (): Promise<PaymentLink[]> => {
      if (!contaCorrenteId) return [];

      const { data, error } = await supabase
        .from('financeiro_payment_links' as any)
        .select('*')
        .eq('conta_corrente_id', contaCorrenteId)
        .order('created_at', { ascending: false });

      if (error) {
        logger.error('Erro ao buscar links de pagamento', error);
        throw error;
      }

      return (data || []) as unknown as PaymentLink[];
    },
    enabled: !!contaCorrenteId && !!user,
  });

  /**
   * Mapeamento de códigos semânticos de erro para mensagens orientativas ao usuário
   */
  const ERROR_MESSAGES: Record<string, string> = {
    PROVIDER_NOT_CONFIGURED: 'Esta conta corrente não tem um provedor de pagamento configurado. Acesse Financeiro > Contas Correntes e configure as credenciais (ex.: Asaas) antes de criar links.',
    MISSING_ACCOUNT: 'Selecione uma conta corrente antes de criar o link.',
    MISSING_ACTION: 'Ação não informada. Recarregue a página e tente novamente.',
    PROVIDER_API_ERROR: 'O provedor de pagamento recusou a operação. Verifique os dados informados e tente novamente.',
    INVALID_PAYLOAD: 'Há campos inválidos no formulário. Revise os dados e tente novamente.',
    UNAUTHORIZED: 'Sua sessão expirou. Faça login novamente.',
  };

  /**
   * Traduz erro técnico em mensagem amigável usando o code semântico quando disponível
   */
  const friendlyPaymentLinkError = (error: Error & { code?: string }): string => {
    if (error.code && ERROR_MESSAGES[error.code]) return ERROR_MESSAGES[error.code];
    return error.message || 'Erro inesperado ao processar o link de pagamento.';
  };

  /**
   * Função auxiliar: invocar edge function com extração robusta do body de erro
   * Mesmo quando o SDK marca como FunctionsHttpError, tentamos ler o body para
   * obter a mensagem real e o code semântico do servidor.
   */
  const invokeEdgeFunction = async (payload: Record<string, unknown>) => {
    const { data, error } = await supabase.functions.invoke('financial-payment-links', {
      body: { ...payload, conta_corrente_id: contaCorrenteId },
    });

    if (error) {
      // Tentar extrair body do FunctionsHttpError (auth 401 / fatal 500)
      let bodyError: { error?: string; code?: string } | null = null;
      try {
        const ctx = (error as { context?: { response?: Response } }).context;
        if (ctx?.response) bodyError = await ctx.response.clone().json();
      } catch {
        // ignore: body indisponível
      }
      const message = bodyError?.error || error.message;
      const code = bodyError?.code;
      throw Object.assign(new Error(message), { code });
    }

    if (!data?.success) {
      const message = typeof data?.error === 'string'
        ? data.error
        : (data?.error?.message || 'Erro desconhecido');
      throw Object.assign(new Error(message), { code: data?.code });
    }

    return data;
  };

  /**
   * Helper para handler de erro padronizado nas mutations
   */
  const handleMutationError = (context: string) => (error: Error & { code?: string }) => {
    logger.error(context, error);
    toast.error(friendlyPaymentLinkError(error), {
      description: error.code ? `Código: ${error.code}` : undefined,
    });
  };

  /**
   * Mutation: Criar link de pagamento
   */
  const createMutation = useMutation({
    mutationFn: async (formData: PaymentLinkFormData) => {
      return invokeEdgeFunction({ action: 'create', ...formData });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
      toast.success('Link de pagamento criado com sucesso!');
    },
    onError: handleMutationError('Erro ao criar link'),
  });

  /**
   * Mutation: Atualizar link de pagamento
   */
  const updateMutation = useMutation({
    mutationFn: async (params: { paymentLinkId: string; providerLinkId: string; data: Partial<PaymentLinkFormData> }) => {
      return invokeEdgeFunction({
        action: 'update',
        payment_link_id: params.paymentLinkId,
        provider_link_id: params.providerLinkId,
        ...params.data,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
      toast.success('Link atualizado com sucesso!');
    },
    onError: handleMutationError('Erro ao atualizar link'),
  });

  /**
   * Mutation: Remover link (soft delete)
   */
  const deleteMutation = useMutation({
    mutationFn: async (params: { paymentLinkId: string; providerLinkId: string }) => {
      return invokeEdgeFunction({
        action: 'delete',
        payment_link_id: params.paymentLinkId,
        provider_link_id: params.providerLinkId,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
      toast.success('Link removido com sucesso!');
    },
    onError: handleMutationError('Erro ao remover link'),
  });

  /**
   * Mutation: Restaurar link removido
   */
  const restoreMutation = useMutation({
    mutationFn: async (params: { paymentLinkId: string; providerLinkId: string }) => {
      return invokeEdgeFunction({
        action: 'restore',
        payment_link_id: params.paymentLinkId,
        provider_link_id: params.providerLinkId,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
      toast.success('Link restaurado com sucesso!');
    },
    onError: handleMutationError('Erro ao restaurar link'),
  });

  /**
   * Mutation: Ativar/desativar link
   */
  const toggleActiveMutation = useMutation({
    mutationFn: async (params: { paymentLinkId: string; providerLinkId: string; active: boolean }) => {
      return invokeEdgeFunction({
        action: 'toggle_active',
        payment_link_id: params.paymentLinkId,
        provider_link_id: params.providerLinkId,
        active: params.active,
      });
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey });
      toast.success(variables.active ? 'Link ativado!' : 'Link desativado!');
    },
    onError: handleMutationError('Erro ao alterar status do link'),
  });

  /**
   * Mutation: Upload de imagem
   */
  const uploadImageMutation = useMutation({
    mutationFn: async (params: { providerLinkId: string; imageData: string; filename: string; main?: boolean }) => {
      return invokeEdgeFunction({
        action: 'upload_image',
        provider_link_id: params.providerLinkId,
        image_data: params.imageData,
        image_filename: params.filename,
        image_main: params.main ?? false,
      });
    },
    onSuccess: () => {
      toast.success('Imagem enviada com sucesso!');
    },
    onError: handleMutationError('Erro ao enviar imagem'),
  });

  /**
   * Mutation: Remover imagem
   */
  const deleteImageMutation = useMutation({
    mutationFn: async (params: { providerLinkId: string; imageId: string }) => {
      return invokeEdgeFunction({
        action: 'delete_image',
        provider_link_id: params.providerLinkId,
        image_id: params.imageId,
      });
    },
    onSuccess: () => {
      toast.success('Imagem removida!');
    },
    onError: handleMutationError('Erro ao remover imagem'),
  });

  /**
   * Mutation: Definir imagem principal
   */
  const setMainImageMutation = useMutation({
    mutationFn: async (params: { providerLinkId: string; imageId: string }) => {
      return invokeEdgeFunction({
        action: 'set_main_image',
        provider_link_id: params.providerLinkId,
        image_id: params.imageId,
      });
    },
    onSuccess: () => {
      toast.success('Imagem principal definida!');
    },
    onError: handleMutationError('Erro ao definir imagem principal'),
  });

  return {
    // Dados
    links: linksQuery.data || [],
    isLoading: linksQuery.isLoading,
    isError: linksQuery.isError,
    refetch: linksQuery.refetch,

    // Mutations CRUD
    createLink: createMutation.mutateAsync,
    isCreating: createMutation.isPending,
    updateLink: updateMutation.mutateAsync,
    isUpdating: updateMutation.isPending,
    deleteLink: deleteMutation.mutateAsync,
    isDeleting: deleteMutation.isPending,
    restoreLink: restoreMutation.mutateAsync,
    isRestoring: restoreMutation.isPending,
    toggleActive: toggleActiveMutation.mutateAsync,
    isToggling: toggleActiveMutation.isPending,

    // Mutations de imagens
    uploadImage: uploadImageMutation.mutateAsync,
    isUploadingImage: uploadImageMutation.isPending,
    deleteImage: deleteImageMutation.mutateAsync,
    isDeletingImage: deleteImageMutation.isPending,
    setMainImage: setMainImageMutation.mutateAsync,
    isSettingMainImage: setMainImageMutation.isPending,
  };
}
